import React from 'react';
import { Page, Text, View, Document, StyleSheet, Image } from '@react-pdf/renderer';
import { Property } from '../../types/property';

interface ClientPDFProps {
  property: Property;
  brandColor?: string;
}

export const ClientPDF: React.FC<ClientPDFProps> = ({ property, brandColor = '#0F766E' }) => {
  const styles = StyleSheet.create({
    page: {
      backgroundColor: '#FFFFFF',
      fontFamily: 'Helvetica',
      padding: 25,
      height: '100%',
    },
    header: {
      marginBottom: 15,
      borderBottomWidth: 1,
      borderBottomColor: '#E5E7EB',
      paddingBottom: 10,
    },
    title: {
      fontSize: 20,
      fontWeight: 'bold',
      color: brandColor,
      marginBottom: 5,
      textAlign: 'center',
    },
    address: {
      fontSize: 10,
      color: '#6B7280',
      textAlign: 'center',
      marginBottom: 15,
    },
    priceBadge: {
      backgroundColor: `${brandColor}20`,
      paddingVertical: 8,
      paddingHorizontal: 20,
      borderRadius: 25,
      borderWidth: 1,
      borderColor: brandColor,
      alignSelf: 'center',
      marginBottom: 20,
    },
    priceText: {
      fontSize: 18,
      fontWeight: 'bold',
      color: brandColor,
      letterSpacing: -0.5,
    },
    section: {
      marginBottom: 15,
    },
    sectionTitle: {
      fontSize: 12,
      fontWeight: 'bold',
      color: '#111827',
      marginBottom: 10,
      textTransform: 'uppercase',
      letterSpacing: 0.8,
      borderLeftWidth: 3,
      borderLeftColor: brandColor,
      paddingLeft: 8,
    },
    infoGrid: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      gap: 10,
      marginBottom: 10,
    },
    infoCard: {
      width: '48%',
      backgroundColor: '#F9FAFB',
      padding: 8,
      borderRadius: 6,
      borderLeftWidth: 3,
      borderLeftColor: brandColor,
    },
    infoLabel: {
      fontSize: 8,
      color: '#6B7280',
      textTransform: 'uppercase',
      letterSpacing: 0.5,
      marginBottom: 3,
    },
    infoValue: {
      fontSize: 11,
      fontWeight: 'bold',
      color: '#111827',
    },
    featuresSection: {
      marginBottom: 15,
    },
    featuresTitle: {
      fontSize: 12,
      fontWeight: 'bold',
      color: '#111827',
      marginBottom: 8,
      textTransform: 'uppercase',
      letterSpacing: 0.8,
      borderLeftWidth: 3,
      borderLeftColor: brandColor,
      paddingLeft: 8,
    },
    featuresList: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      gap: 6,
    },
    featureBadge: {
      backgroundColor: `${brandColor}15`,
      paddingHorizontal: 10,
      paddingVertical: 4,
      borderRadius: 15,
      borderWidth: 1,
      borderColor: `${brandColor}40`,
    },
    featureText: {
      fontSize: 9,
      color: brandColor,
      fontWeight: '500',
    },
    photosSection: {
      marginTop: 10,
    },
    photosTitle: {
      fontSize: 12,
      fontWeight: 'bold',
      color: '#111827',
      marginBottom: 8,
      textTransform: 'uppercase',
      letterSpacing: 0.8,
      borderLeftWidth: 3,
      borderLeftColor: brandColor,
      paddingLeft: 8,
    },
    photosGrid: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      gap: 8,
    },
    photo: {
      width: '31.5%',
      height: 80,
      borderRadius: 6,
      overflow: 'hidden',
    },
    photoPlaceholder: {
      width: '31.5%',
      height: 80,
      backgroundColor: '#F3F4F6',
      borderRadius: 6,
      justifyContent: 'center',
      alignItems: 'center',
    },
    placeholderText: {
      fontSize: 9,
      color: '#9CA3AF',
      textAlign: 'center',
    },
    description: {
      fontSize: 10,
      color: '#4B5563',
      lineHeight: 1.5,
      marginTop: 10,
      textAlign: 'justify',
    },
    footer: {
      marginTop: 15,
      textAlign: 'center',
      fontSize: 8,
      color: '#9CA3AF',
      borderTopWidth: 1,
      borderTopColor: '#E5E7EB',
      paddingTop: 8,
    },
  });

  const getEquipments = () => {
    const equip = [];
    if (property.features?.hasElevator || property.has_elevator) equip.push('Ascenseur');
    if (property.features?.hasParking || property.has_parking) equip.push('Parking');
    if (property.features?.hasBalcony || property.has_balcony) equip.push('Balcon');
    if (property.features?.hasTerrace || property.has_terrace) equip.push('Terrasse');
    if (property.features?.hasAirConditioning || property.has_air_conditioning) equip.push('Climatisation');
    if (property.features?.heatingType || property.heating_type) {
      equip.push(`Chauffage ${property.features?.heatingType || property.heating_type}`);
    }
    return equip;
  };

  const generateDescription = () => {
    const area = Math.floor(property.features?.area || property.area || 0);
    const rooms = property.features?.rooms || property.rooms || 0;
    const floor = property.features?.floor || property.floor;
    const propertyType = property.type || 
      (property.property_category && property.property_subtype 
        ? `${property.property_category}${property.property_subtype !== 'Simple' ? ' ' + property.property_subtype : ''}`
        : property.property_category) || 'bien';
    let desc = `Ce ${propertyType.toLowerCase()} de ${area} m² offre ${rooms} pièce${rooms > 1 ? 's' : ''}`;
    if (floor !== undefined && floor > 0) {
      desc += `, situé au ${floor}${floor === 1 ? 'er' : 'ème'} étage`;
    }
    desc += '. ';
    const features = [];
    if (property.features?.hasBalcony || property.has_balcony) features.push('un balcon');
    if (property.features?.hasTerrace || property.has_terrace) features.push('une terrasse');
    if (property.features?.hasParking || property.has_parking) features.push('un parking');
    if (features.length > 0) {
      desc += `Il dispose de ${features.join(', ')}. `;
    }
    desc += `Idéalement situé, ce bien allie confort et praticité.`;
    return desc;
  };

  const formatPrice = () => {
    const price = Math.floor(property.price || 0);
    return price.toLocaleString('fr-FR').replace(/\s/g, ' ') + ' DA';
  };

  // Fonction pour obtenir le type de bien de manière robuste
  const getPropertyType = () => {
    // Priorité à property_category + property_subtype
    if (property.property_category) {
      return property.property_subtype && property.property_subtype !== 'Simple'
        ? `${property.property_category} ${property.property_subtype}`
        : property.property_category;
    }
    // Fallback sur property.type
    return property.type || 'Bien immobilier';
  };

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        {/* En-tête */}
        <View style={styles.header}>
          <Text style={styles.title}>
            {getPropertyType()}
          </Text>
          <Text style={styles.address}>{property.address || 'Adresse non spécifiée'}</Text>
          <View style={styles.priceBadge}>
            <Text style={styles.priceText}>{formatPrice()}</Text>
          </View>
        </View>

        {/* Caractéristiques */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Caractéristiques</Text>
          <View style={styles.infoGrid}>
            <View style={styles.infoCard}>
              <Text style={styles.infoLabel}>Surface</Text>
              <Text style={styles.infoValue}>{Math.floor(property.features?.area || property.area || 0)} m²</Text>
            </View>
            <View style={styles.infoCard}>
              <Text style={styles.infoLabel}>Pièces</Text>
              <Text style={styles.infoValue}>{property.features?.rooms || property.rooms || 0}</Text>
            </View>
            {(property.features?.floor !== undefined || property.floor !== undefined) && (
              <View style={styles.infoCard}>
                <Text style={styles.infoLabel}>Étage</Text>
                <Text style={styles.infoValue}>{property.features?.floor || property.floor || 'RDC'}</Text>
              </View>
            )}
            {(property.features?.constructionYear || property.construction_year) && (
              <View style={styles.infoCard}>
                <Text style={styles.infoLabel}>Année</Text>
                <Text style={styles.infoValue}>{property.features?.constructionYear || property.construction_year}</Text>
              </View>
            )}
            {(property.features?.bedrooms || property.bedrooms) && (
              <View style={styles.infoCard}>
                <Text style={styles.infoLabel}>Chambres</Text>
                <Text style={styles.infoValue}>{property.features?.bedrooms || property.bedrooms}</Text>
              </View>
            )}
            {(property.features?.bathrooms || property.bathrooms) && (
              <View style={styles.infoCard}>
                <Text style={styles.infoLabel}>Salles de bain</Text>
                <Text style={styles.infoValue}>{property.features?.bathrooms || property.bathrooms}</Text>
              </View>
            )}
          </View>
        </View>

        {/* Équipements */}
        <View style={styles.featuresSection}>
          <Text style={styles.featuresTitle}>Équipements</Text>
          <View style={styles.featuresList}>
            {getEquipments().map((equipment, index) => (
              <View key={index} style={styles.featureBadge}>
                <Text style={styles.featureText}>{equipment}</Text>
              </View>
            ))}
          </View>
        </View>

        {/* Description */}
        <Text style={styles.description}>
          {property.description || generateDescription()}
        </Text>

        {/* Photos (max 6 pour tenir sur une page) */}
        <View style={styles.photosSection}>
          <Text style={styles.photosTitle}>Galerie photo</Text>
          <View style={styles.photosGrid}>
            {property.photos && property.photos.length > 0 ? (
              property.photos.slice(0, 6).map((photo, index) => (
                <Image key={index} style={styles.photo} src={photo} />
              ))
            ) : (
              <View style={styles.photoPlaceholder}>
                <Text style={styles.placeholderText}>Aucune photo disponible</Text>
              </View>
            )}
          </View>
        </View>

        {/* Pied de page */}
        <Text style={styles.footer}>
          Fiche acquéreur générée le {new Date().toLocaleDateString('fr-FR')} • Réf: {property.id}
        </Text>
      </Page>
    </Document>
  );
};